import { motion } from "framer-motion";
import mascotBill from "@/assets/landing/dollarbill-mascot.png";

export const AICFOSection = () => {
  return (
    <section className="py-24 px-6 bg-white" id="about">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-midnight mb-4 tracking-tight">
            Meet Dollar <span className="text-vault-green">Bill</span>. <span className="text-vault-green">Your CFO.</span>
          </h2>
          <p className="text-lg text-slate-brand max-w-2xl mx-auto">
            Stop guessing. Get answers to the questions that actually matter, instantly.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Mascot card with status pill */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <div className="relative">
              {/* Card container */}
              <motion.div
                className="bg-white rounded-3xl border border-fog shadow-xl overflow-hidden max-w-[400px]"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <img
                  src={mascotBill}
                  alt="Dollar Bill — Your CFO mascot"
                  className="w-full"
                />
              </motion.div>

              {/* Suggestion pill — top left, overlapping the card edge */}
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.4 }}
                className="absolute bg-white rounded-full shadow-lg border border-fog px-4 py-2.5 flex items-center gap-2"
                style={{ left: "-33px", top: "-25px" }}
              >
                <span className="text-base">💡</span>
                <span className="text-midnight font-medium text-sm">
                  Switch to vendor B — save $240/mo
                </span>
              </motion.div>

              {/* Status pill — bottom right, overlapping the card edge */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5, duration: 0.4 }}
                className="absolute bg-white rounded-full shadow-lg border border-fog px-4 py-2.5 flex items-center gap-2"
                style={{ left: "174px", top: "386px" }}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-vault-green animate-pulse" />
                <span className="text-vault-green font-semibold text-sm tracking-wide uppercase">
                  Bill is analyzing...
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* Right — Text content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-8"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-midnight tracking-tight">
              Always know why, and what to do.
            </h3>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-vault-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-vault-green font-bold text-sm">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight mb-1">
                    Bill learns your business
                  </h4>
                  <p className="text-slate-brand text-sm leading-relaxed">
                    Connect your POS and bank data. Bill studies your numbers —
                    margins, vendors, menu items, delivery channels — and builds
                    a complete financial picture.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-vault-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-vault-green font-bold text-sm">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight mb-1">
                    Ask anything, in plain English
                  </h4>
                  <p className="text-slate-brand text-sm leading-relaxed">
                    "Why did I make less money yesterday?" — Bill gives you a
                    clear answer with specific data, not jargon. Summary, why it
                    happened, what it means, what to do.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-vault-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-vault-green font-bold text-sm">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-midnight mb-1">
                    Five minutes with Bill. The rest of the night is yours.
                  </h4>
                  <p className="text-slate-brand text-sm leading-relaxed">
                    No more 11pm spreadsheets. No more guessing about margins.
                    Bill watches your numbers so you don't have to.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
